import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import { orderApi } from '../services';
import { formatMoney } from '../utils/utils';

function OrderDetailPage({ onNavigate }) {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadOrderDetail();
  }, [id]);

  const loadOrderDetail = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use the new getOrderDetail method with order_id query parameter
      const response = await orderApi.getOrderDetail(id);
      
      if (response.payload) {
        setOrder(response.payload);
      } else {
        setError('Không tìm thấy đơn hàng');
      }
    } catch (error) {
      console.error('Error loading order detail:', error);
      setError('Không thể tải thông tin đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!order) return;

    setCheckingOut(true);
    setError('');
    setSuccess('');

    try {
      const response = await orderApi.checkoutOrder(order.order_id || order.id);
      
      if (response.success || response.payload) {
        setSuccess('Checkout thành công!');
        // Reload order to get updated status
        await loadOrderDetail();
      } else {
        setError('Checkout không thành công. Vui lòng thử lại.');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      setError('Có lỗi xảy ra khi checkout. Vui lòng thử lại.');
    } finally {
      setCheckingOut(false);
    }
  };

  const calculateTotal = () => {
    if (!order) return 0;
    
    // Calculate total from regular products only (exclude redeemed products)
    const regularProductsTotal = order.list_products ? 
      order.list_products.reduce((sum, item) => sum + (item.quantity * (item.price || 0)), 0) : 0;
    
    // Redeemed products don't contribute to total cost
    return regularProductsTotal;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return "#7ED957";
      case 2:
        return "#F9A23B";
      case 2:
        return "#ff4444";
      default:
        return "#888";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 2:
        return "Đã check-out";
      case 0:
        return "Đã check-in";
      case 3:
        return "Đã hủy";
      default:
        return status || "Không xác định";
    }
  };

  const canCheckout = () => {
    if (!order) return false;
    const status = order.status;
    return status === 'pending' || status === 'đang xử lý' || status === 'đã check-in';
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div>Đang tải thông tin đơn hàng...</div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#ff4444', marginBottom: 16 }}>{error}</div>
        <button 
          onClick={() => onNavigate('/order-history')}
          style={{ padding: '8px 16px', background: '#5b6754', color: '#fff', border: 'none', borderRadius: 20 }}
        >
          Quay lại lịch sử
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 40 }}>
      <div style={{ background: '#fff', width: 340, minHeight: 600, borderRadius: 8, padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Header onCartClick={() => onNavigate && onNavigate('/order-history')} onMenuClick={() => onNavigate && onNavigate('/')} />
        
        <div style={{ width: '100%', fontWeight: 700, fontSize: 20, margin: '16px 0 24px 0', color: '#5b6754' }}>
          Chi tiết đơn hàng
        </div>

        {order && (
          <div style={{ width: '100%', background: '#f8f8f8', borderRadius: 12, padding: 16, marginBottom: 18, boxShadow: '0 2px 8px rgba(91,103,84,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>{order.order_code || order.id}</div>
              <div style={{ color: '#888', fontSize: 14 }}>
                {formatDate(order.create_datetime || order.created_datetime || order.created_at || order.date)}
              </div>
            </div>
            
            <div style={{ 
              marginBottom: 6, 
              color: getStatusColor(order.status), 
              fontWeight: 600 
            }}>
              {getStatusText(order.status)}
            </div>
            
            <div style={{ borderTop: '1px solid #e0e0e0', margin: '10px 0' }} />
            
            {/* Regular products */}
            {order.list_products && order.list_products.length > 0 && (
              order.list_products.map((item, idx) => (
                <div key={`regular-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 16 }}>
                    {item.name} <span style={{ color: '#888' }}>x{item.quantity}</span>
                  </div>
                  <div style={{ fontWeight: 600, color: '#5b6754', fontSize: 16 }}>
                    {formatMoney(item.quantity * (item.price || 0))}đ
                  </div>
                </div>
              ))
            )}
            
            {/* Redeemed products - no price shown */}
            {order.list_product_redeem && order.list_product_redeem.length > 0 && (
              order.list_product_redeem.map((item, idx) => (
                <div key={`redeemed-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 16 }}>
                    {item.name} <span style={{ color: '#888' }}>x{item.quantity}</span>
                    <span style={{ color: '#F9A23B', fontSize: 14, marginLeft: 8 }}>(Đã đổi)</span>
                  </div>
                  <div style={{ fontWeight: 600, color: '#F9A23B', fontSize: 16 }}>
                    Miễn phí
                  </div>
                </div>
              ))
            )}
            
            {(!order.list_products || order.list_products.length === 0) && 
             (!order.list_product_redeem || order.list_product_redeem.length === 0) && (
              <div style={{ color: '#888', fontSize: 14 }}>Không có thông tin sản phẩm</div>
            )}
            
            <div style={{ borderTop: '1px solid #e0e0e0', margin: '10px 0' }} />
            
            {/* Overtime surcharge (if any) */}
            {order && Number(order.overtime_amount) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span>Phụ phí quá giờ</span>
                <span style={{ color: '#ff4444', fontWeight: 600 }}>+{formatMoney(order.overtime_amount)}đ</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: 18 }}>
              <span>Tổng cộng</span>
              <span style={{ color: '#5b6754' }}>
                {order.total_amount ? 
                  formatMoney(Number(order.total_amount) + Number(order.overtime_amount)) : 
                  formatMoney(calculateTotal() + (Number(order.overtime_amount) || 0))
                }đ
              </span>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {success && (
          <div style={{ 
            width: '100%',
            background: '#e8f5e8',
            border: '1px solid #4CAF50',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            color: '#2e7d2e',
            fontSize: 14,
            textAlign: 'center'
          }}>
            {success}
          </div>
        )}

        {error && (
          <div style={{ 
            width: '100%',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            color: '#c33',
            fontSize: 14,
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {/* Checkout Button
        {canCheckout() && (
          <button 
            style={{
              ...btnStyle,
              opacity: checkingOut ? 0.7 : 1,
              cursor: checkingOut ? 'not-allowed' : 'pointer'
            }}
            onClick={handleCheckout}
            disabled={checkingOut}
          >
            {checkingOut ? 'Đang xử lý...' : 'Check out'}
          </button>
        )}

        {!canCheckout() && order && (
          <div style={{ 
            color: '#888', 
            fontSize: 14, 
            textAlign: 'center',
            padding: '12px 0'
          }}>
            Đơn hàng này không thể checkout
          </div>
        )} */}
      </div>
    </div>
  );
}

const btnStyle = {
  width: 180,
  height: 44,
  background: '#5b6754',
  color: '#fff',
  border: 'none',
  borderRadius: 24,
  fontSize: 18,
  fontWeight: 300,
  margin: '12px 0',
  cursor: 'pointer',
  fontFamily: 'NeueEinstellung',
};

export default OrderDetailPage; 